import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { IRoomCategory } from "../../models/BaseModel";
import { baseUrl, Fetch, ILazyParams } from "../../shared/Fetch";
import { Search } from "../../shared/Search";
import { IOpenForm } from "./RoomCategory";
import { format } from "date-fns";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { Tooltip } from "primereact/tooltip";

interface Iprops {
    setModal: (modal: IOpenForm) => void,
    toast: any,
    reload: boolean,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Table = (props: Iprops) => {
    const [tableData, setTableData] = useState({ data: [], count: 0, loading: false } as { data: IRoomCategory[], count: number, loading: boolean });
    const [text, setText] = useState<string | null>(null);
    const [openDescription, setOpenDescription] = useState({ open: false, singleData: {} } as { open: boolean, singleData: any });
    const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
    const [imageDialog, setImageDialog] = useState<{ open: boolean, images: { id: number, imageFileName: string }[], title?: string }>({ open: false, images: [], title: '' });
    const fileUploadRef = useRef<FileUpload | null>(null);


    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });

    useEffect(() => {
        loadTableData();
    }, [text, lazyParams, props.reload]);

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTableData((prev) => ({ ...prev, loading: true, }));
        const response = await Fetch("RoomCategory", "GET", null, `lazyParams=${JSON.stringify(lazyParams)}${text ? '&text=' + text : ''}`, props.toast);
        if (response) {
            const data: { roomCategory: IRoomCategory[], count: number } = await response.json();
            setTableData({ data: data.roomCategory, count: data.count, loading: false });
        } else {
            setTableData((prev) => ({ ...prev, loading: false, }));
        }
    }

    const openNew = () => {
        props.setModal({ open: true, singleData: {} });
    };

    const openEdit = (rowDatauser: IRoomCategory) => {
        props.setModal({ open: true, singleData: rowDatauser });
    };

    const openDescriptions = (rowDatauser: IRoomCategory) => {
        setOpenDescription({ open: true, singleData: rowDatauser });
    };

    const Remove = async (rowData: IRoomCategory) => {
        setTableData((prev) => ({ ...prev, loading: true, }));
        const response = await Fetch(`RoomCategory/${rowData.id}`, "DELETE", null, null, props.toast);
        if (response) {
            props.toast.current.show({ severity: 'success', detail: " Deleted", life: 3000 });
            loadTableData();
        }
    }

    const rejectFunc = () => { }

    const confirmDelete = (rowData: IRoomCategory) => {
        confirmDialog({
            message: `Are you sure you want to delete "${rowData.title}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => Remove(rowData),
            reject: () => rejectFunc()
        });
    }

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-4">
            <div className="flex gap-4 w-full">
                <Button style={{ width: '80px', height: '30px' }} label="New" icon="pi pi-plus" className="p-button-sm p-button-rounded p-button-success" onClick={openNew} />
                <div className="w-full flex align-items-center justify-content-center">
                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontFamily: 'Rancho', letterSpacing: 1 }}>Room Category</h4>
                </div>
            </div>
            <div className="flex gap-4">
                <div className="p-input-icon-left">
                    <Search setSearch={setText} />
                </div>
                <Button style={{ width: '80px', height: '30px' }} label="Export" icon="pi pi-upload" className="p-button-sm p-button-rounded p-button-help" onClick={exportCSV} />
            </div>
        </div>
    );

    const hideDescriptionDialog = () => {
        setOpenDescription({ open: false, singleData: {} })
    }


    const onUpload = async (event: any) => {
        const file = event.files[0];
        const fileType = file.type.split("/")[1];

        if (["png", "jpeg", "jpg"].includes(fileType)) {
            if (file.size > 2 * 1024 * 1024) {
                props.toast.current.show({ severity: 'error', content: "Image File should be less than 2 MB." });
                event.options.clear();
                return;
            }

            const formData = new FormData();
            formData.append('images', file);
            formData.append('roomCategoryId', String(selectedRoomId)); // Attach the Room ID
            formData.append('imageType', fileType);
            const response = await fetch(`${baseUrl}Image`, {
                method: "POST",
                headers: {
                    'Authorization': `Bearer ${JSON.parse(sessionStorage.getItem('auth') + "")?.token}`
                },
                body: formData
            });

            if (response.ok) {
                props.toast.current.show({ severity: 'success', detail: "Image Uploaded", life: 3000 });
                loadTableData();
            } else {
                props.toast.current.show({ severity: 'error', detail: "Failed to upload image", life: 3000 });
            }

            event.options.clear();
            setSelectedRoomId(null)
        } else {
            props.toast.current.show({ severity: 'error', content: "Please Choose A Valid Image File." });
            event.options.clear();
        }
    };

    const actionBodyTemplate = (rowData: any) => {

        return (
            <div className="flex align-items-center gap-2">
                <Tooltip target=".action-btn" position="top" showDelay={300} />
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-button-sm action-btn" tooltip="Edit" onClick={() => openEdit(rowData)} />
                <Button icon="pi pi-image" className="p-button-rounded p-button-info p-button-sm action-btn" tooltip="View Images" onClick={() => showRoomImages(rowData)} />
                <Button icon="pi pi-eye" className="p-button-rounded p-button-warning p-button-sm action-btn" tooltip="View Description" onClick={() => openDescriptions(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-sm action-btn" tooltip="Delete" onClick={() => confirmDelete(rowData)} />
                <Button icon="pi pi-plus" className="p-button-rounded p-button-help p-button-sm" tooltip="Upload Images"
                    onClick={() => { setSelectedRoomId(rowData.id); fileUploadRef.current?.getInput().click() }} />
                <FileUpload ref={fileUploadRef} mode="basic" name="image" accept="image/*" customUpload
                    uploadHandler={(event) => {
                        onUpload(event);
                    }} auto style={{ display: "none" }} />
            </div>
        );
    };

    const showRoomImages = async (rowData: IRoomCategory) => {
        const response = await Fetch(`Image/GetRoomImages?roomCategoryId=${rowData.id}`, "GET", null, null, props.toast);
        if (response) {
            const images: { imageFileName: string, id: number }[] = await response.json();
            setImageDialog({ open: true, images, title: rowData.title });
        }
    };

    const deleteImage = async (imageId: number) => {
        const response = await Fetch(`Image/DeleteImage/${imageId}`, "DELETE", null, null, props.toast);
        if (response) {
            props.toast.current.show({ severity: 'success', detail: 'Image deleted', life: 3000 });
            setImageDialog(prev => ({
                ...prev,
                images: prev.images.filter(img => img.id !== imageId)
            }));
        }
    };

    return (
        <>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small" stripedRows scrollable scrollHeight="75vh" exportFilename={format(new Date(), 'dd-MM-yyyy hh_mm_ss a').toUpperCase()}
                value={tableData.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={tableData.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={tableData.loading}
            >
                <Column header="SN." body={(data, options) => options.rowIndex + 1} />
                <Column field="title" header="Title" sortable />
                <Column field="beds" header="No. Of Beds" sortable />
                <Column field="bathrooms" header="No. Of Bathrooms" sortable />
                <Column field="area" header="Area" sortable />
                <Column field="rate" header="Rate" sortable />
                <Column field="keyFeatures" header="Key Features" sortable />
                <Column body={actionBodyTemplate} />
            </DataTable>
            <ConfirmDialog />

            <Dialog visible={openDescription.open} style={{ width: '30vw' }} header="View Description" className="p-fluid" onHide={hideDescriptionDialog}>
                <div
                    dangerouslySetInnerHTML={{ __html: openDescription.singleData.description }}
                />
            </Dialog>


            <Dialog visible={imageDialog.open} onHide={() => setImageDialog({ open: false, images: [], title: '' })} header={`Images for: ${imageDialog.title}`} style={{ width: '50vw' }}   >
                <div className="grid">
                    {imageDialog.images.length > 0 ? (
                        imageDialog.images.map((img, index) => (
                            <div key={index} className="col-4" style={{ padding: '10px', position: 'relative' }}>
                                <img src={`${baseUrl}image/streamImage?fileName=${img.imageFileName}`} alt="Room" style={{ width: '100%', borderRadius: '8px', objectFit: 'cover' }} />
                                <Button icon="pi pi-trash" className="p-button-danger p-button-sm" style={{ position: 'absolute', top: '5px', right: '5px', borderRadius: '50%', padding: '0.2rem' }} onClick={() => deleteImage(img.id)} tooltip="Delete Image" />
                            </div>
                        ))
                    ) : (
                        <p>No images available for this room.</p>
                    )}
                </div>
            </Dialog>
        </>
    );
}
